import React, {FC, useEffect, useState} from 'react';

export interface ExpanderChildProps {
  expanded?: boolean;
  onToggleExpanded?: () => void;
}

export interface ExpanderGroupProps extends React.HTMLAttributes<HTMLDivElement> {
  expandedIndex?: number;
  expandedIndexes?: number[];
  multiExpanded?: boolean;
  children?: React.ReactNode;
  onExpanderToggled?: (index?: number, state?: 'collapsed' | 'expanded') => void;
}

type ExpandedListInfluencingProps = Pick<ExpanderGroupProps, 'expandedIndex' | 'expandedIndexes' | 'multiExpanded'>;

function computeExpandedListFromProps({expandedIndex, expandedIndexes, multiExpanded}: ExpandedListInfluencingProps): number[] {
  if (Array.isArray(expandedIndexes) && multiExpanded) {
    return expandedIndexes;
  }

  if (typeof expandedIndex === 'number') {
    return [expandedIndex];
  }

  return [];
}

const ExpanderGroup: FC<ExpanderGroupProps> = props => {
  const {expandedIndex, expandedIndexes, multiExpanded = false, onExpanderToggled, children, ...otherProps} = props;

  const initialState = computeExpandedListFromProps(props);
  const [expandedList, setExpandedList] = useState(initialState);

  function handleToggleExpander(idx: number): void {
    const isAlreadyExpanded = expandedList.includes(idx);

    onExpanderToggled && onExpanderToggled(idx, isAlreadyExpanded ? 'collapsed' : 'expanded');

    if (multiExpanded) {
      const indexes = isAlreadyExpanded ? expandedList.filter(i => i !== idx) : [...expandedList, idx];
      setExpandedList(indexes);
    } else {
      setExpandedList(isAlreadyExpanded ? [] : [idx]);
    }
  }

  useEffect(() => {
    const newState = computeExpandedListFromProps({expandedIndex, expandedIndexes, multiExpanded});
    setExpandedList(newState);
  }, [expandedIndex, expandedIndexes, multiExpanded]);

  const childrenWithProps = React.Children.map(children, (child, idx) => {
    if (!React.isValidElement(child)) {
      return null;
    }

    return React.cloneElement(child, {
      expanded: expandedList.includes(idx),
      onToggleExpanded: () => handleToggleExpander(idx)
    });
  });

  return <div {...otherProps}>{childrenWithProps}</div>;
};

export default ExpanderGroup;
